import React, { useState } from "react";
import {
    Typography,
    Box,
    styled,
    Skeleton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MuiAccordionSummary, {
    AccordionSummaryProps,
    accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useNavigate } from "react-router-dom";

// --- Styled Accordion Components ---

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: '0.9rem' }} />} // Use the down arrow icon
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: '#FFEBD5',
    flexDirection: 'row', // Content then icon
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
        transform: 'rotate(0deg)', // Down arrow points down by default
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
        transform: 'rotate(180deg)', // Rotate 180 degrees to point up when expanded
    },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: 0,
        flexGrow: 1,
        justifyContent: 'flex-start',
        paddingRight: theme.spacing(1), // Space between text and arrow
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none'
}));

// --- Authors Accordion Component ---
export interface LanguageAccordionProps {
    languages: LanguageItem[];
    loadingLanguages: boolean;
}
export interface LanguageItem {
    id: number;
    name: string;
}
const LanguageAccordion: React.FC<LanguageAccordionProps> = ({ languages, loadingLanguages }) => {
    const [expanded, setExpanded] = useState<string | false>(false);

    const { t } = useTranslation();

    // Helper to set both expanded and productFor
    const handleChange = (panelId: string, productForValue: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? panelId : false);
    };
const navigate = useNavigate();
    return (
        <>
            <Box sx={{ p: 2 }} >
                {loadingLanguages ? (
                    <Box sx={{ p: 2 }}>
                        <Accordion
                            expanded={false}
                            style={{ backgroundColor: "#FFEBD5", margin: -14, marginTop: -35, border: "2px solid white" }}
                        >
                            <AccordionSummary expandIcon={null} sx={{ cursor: 'default' }}>
                                <Skeleton variant="text" width="60%" height={28} />
                            </AccordionSummary>
                            <AccordionDetails style={{ backgroundColor: "white" }}>
                                {Array.from(new Array(5)).map((_, index) => (
                                    <Box key={index} sx={{ borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                                        <Skeleton variant="text" width={`${80 - index * 10}%`} height={20} />
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                ) : (
                    <>
                        {languages.length === 0 ? ( // Simplified condition for no data
                            <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                                No Authors or Kalyans found.
                            </Typography>
                        ) : (
                            <>
                                {/* E_books_tr Accordion */}
                                <Accordion
                                    defaultExpanded
                                    style={{ backgroundColor: "#FFEBD5", margin: -14, marginTop: -35, border: "2px solid white" }}
                                >
                                    <AccordionSummary>
                                        <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("E_books_tr")}</Typography>
                                    </AccordionSummary>

                                    <AccordionDetails style={{ backgroundColor: "white" }}>
                                        {languages.length > 0 ? (
                                            languages.map((language: LanguageItem) => (
                                                <Box key={language.id} onClick={()=>navigate("/books",{state:{selectedLanguageId:language.id, languageName:language.name}})} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2, cursor: 'pointer' }}>
                                                    <StarBorderIcon sx={{ fontSize: 14, color: '#f97316' }} />
                                                    <Typography component="span">{language.name}</Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                                                No E-book Authors found.
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>

                            </>
                        )}
                    </>
                )}
            </Box>

        </>
    );

};
export default LanguageAccordion;
